import { prepareSentryEvent } from "@gettactic/helpers/src/errors/prepareSentryEvent";
import type { ErrorEvent, EventHint } from "@sentry/nextjs";

// The DSN is not a secret: the browser SDK sends it with every event.
const DEFAULT_SENTRY_DSN =
  "https://dd939499e5cb4be5a00f92b6afa88d36@o532705.ingest.sentry.io/5651915";

// "production" for the live app, so alerts can filter on it. Vercel previews
// report as "preview" and never page anyone.
const environment =
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ||
  process.env.NEXT_PUBLIC_VERCEL_ENV ||
  "development";

/**
 * Options shared by the browser, server and edge Sentry SDKs.
 */
export const sentryOptions = {
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || DEFAULT_SENTRY_DSN,
  // Local dev servers would otherwise file their half-written code and local
  // API failures as production issues. Set NEXT_PUBLIC_SENTRY_ENVIRONMENT to
  // test Sentry locally.
  enabled:
    process.env.NEXT_PUBLIC_SENTRY_DISABLE !== "1" &&
    environment !== "development",
  environment,
  // Tracing is only used to debug slow pages, a small sample is enough.
  tracesSampleRate: 0.05,
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    // Stale tab after a deploy: the chunk it asks for no longer exists.
    "ChunkLoadError",
    /Loading chunk [\d]+ failed/,
    // Tauri desktop app asking for permissions the web build doesn't have.
    /not allowed by ACL/,
    // Outlook SafeLinks / Defender scanners opening emailed links in a headless
    // CefSharp browser, whose .NET bridge rejects with this string.
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/
  ],
  denyUrls: [
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-(web-)?extension:\/\//
  ],
  beforeSend: (event: ErrorEvent, hint: EventHint): ErrorEvent | null =>
    prepareSentryEvent(event, hint.originalException)
};
